import type { CaptureData, ElementMeta, ProjectVersion, ResolvedBugSmashConfig, WidgetPin } from './types.js';

const DEFAULT_BUGSMASH_API_BASE_URL = 'https://api.bugsmash.io';

type RuntimeApiConfig = {
  baseAppUrl?: string;
};

function normalizeApiBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

function getApiBaseUrl(config?: RuntimeApiConfig): string {
  return normalizeApiBaseUrl(
    config?.baseAppUrl || import.meta.env.VITE_API_BASE_URL || DEFAULT_BUGSMASH_API_BASE_URL,
  );
}

export interface SubmitOptions {
  text: string;
  feedbackByName: string;
  feedbackByEmail?: string;
  clickX: number;
  clickY: number;
  /** Fraction of element width from its left edge (0–1) */
  relX?: number;
  /** Fraction of element height from its top edge (0–1) */
  relY?: number;
  url: string;
  screenSize: string;
  elementMeta: ElementMeta;
  /** null if capture timed out — review is still submitted without screenshot */
  capture: CaptureData | null;
  /** Browser-rendered viewport PNG used as immediate screenshot fallback */
  viewportScreenshot?: Blob | null;
  /** Optional reviewer-selected image attachment from the paperclip control */
  attachment?: File | null;
}

export interface SubmitResult {
  /** Server-assigned feedback number for this project */
  number: number;
  feedbackId: string;
  projectFileId: string;
}

export async function fetchProjectVersions(
  config: Pick<ResolvedBugSmashConfig, 'projectId'> & RuntimeApiConfig,
): Promise<ProjectVersion[]> {
  const url = new URL(`${getApiBaseUrl(config)}/api/v2/feedback/widget/versions`);
  url.searchParams.set('projectId', config.projectId);

  const response = await fetch(url.toString());
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(body.message ?? `HTTP ${response.status}`);
  }

  const json = await response.json() as { data?: ProjectVersion[] };
  return json.data ?? [];
}

export async function fetchWidgetPins(
  config: Pick<ResolvedBugSmashConfig, 'projectId' | 'projectVersionId'> & RuntimeApiConfig,
  pageUrl: string,
): Promise<WidgetPin[]> {
  const url = new URL(`${getApiBaseUrl(config)}/api/v2/feedback/widget/pins`);
  url.searchParams.set('projectId', config.projectId);
  url.searchParams.set('url', pageUrl);
  if (config.projectVersionId) {
    url.searchParams.set('projectVersionId', config.projectVersionId);
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(body.message ?? `HTTP ${response.status}`);
  }

  const json = await response.json() as { data?: WidgetPin[] };
  return json.data ?? [];
}

/**
 * Submits a widget review to POST /v2/feedback/widget/add-review.
 * Returns the server-assigned feedback number for the pin label.
 */
export async function submitReview(
  config: ResolvedBugSmashConfig & RuntimeApiConfig,
  opts: SubmitOptions,
): Promise<SubmitResult> {
  const url = `${getApiBaseUrl(config)}/api/v2/feedback/widget/add-review`;

  const form = new FormData();
  form.append('projectId', config.projectId);
  if (config.projectVersionId) form.append('projectVersionId', config.projectVersionId);
  form.append('text', opts.text);
  form.append('feedbackByName', opts.feedbackByName);
  if (opts.feedbackByEmail) form.append('feedbackByEmail', opts.feedbackByEmail);
  form.append('url', opts.url);
  form.append('screenSize', opts.screenSize);
  form.append('status', 'A');

  // Element-relative anchor used to reposition pins on reload/other-reviewer
  form.append('relX', String(opts.relX ?? 0.5));
  form.append('relY', String(opts.relY ?? 0.5));

  // Element metadata for FeedbackWeb record
  form.append('domPath', opts.elementMeta.domPath);
  form.append('tagName', opts.elementMeta.tagName);
  form.append('elementWidth', String(opts.elementMeta.elementWidth));
  form.append('elementHeight', String(opts.elementMeta.elementHeight));
  form.append('textContent', opts.elementMeta.textContent);

  if (opts.capture) {
    form.append('cx', String(opts.capture.cx));
    form.append('cy', String(opts.capture.cy));
    form.append('screenshotHtml', opts.capture.html);
    form.append('isCompressed', opts.capture.isCompressed ? '1' : '0');
    form.append('screenshotBoundingBox', JSON.stringify(opts.capture.boundingBox));
    form.append('screenshotViewport', JSON.stringify(opts.capture.viewport));
    form.append('screenshotEnvironment', JSON.stringify(opts.capture.screenshotEnvironment));
  } else {
    // No capture: send normalized click position as best-effort
    form.append('cx', String(opts.clickX / window.innerWidth));
    form.append('cy', String(opts.clickY / window.innerHeight));
  }

  if (opts.viewportScreenshot) {
    form.append('screenshot', opts.viewportScreenshot, 'widget-screenshot.png');
  }
  if (opts.attachment) {
    form.append('attachment', opts.attachment);
  }

  const response = await fetch(url, { method: 'POST', body: form });

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(body.message ?? `HTTP ${response.status}`);
  }

  const json = await response.json() as {
    data?: { number?: number; feedbackId?: string; projectFileId?: string };
  };

  return {
    number: json.data?.number ?? 0,
    feedbackId: json.data?.feedbackId ?? '',
    projectFileId: json.data?.projectFileId ?? '',
  };
}

export async function submitReviewScreenshot(
  config: ResolvedBugSmashConfig & RuntimeApiConfig,
  opts: Pick<SubmitOptions, 'capture' | 'clickX' | 'clickY' | 'viewportScreenshot'> & {
    feedbackId: string;
    projectFileId: string;
  },
): Promise<void> {
  if (!opts.capture && !opts.viewportScreenshot) return;

  const url = `${getApiBaseUrl(config)}/api/v2/feedback/widget/add-review-screenshot`;
  const form = new FormData();
  form.append('projectId', config.projectId);
  form.append('feedbackId', opts.feedbackId);
  form.append('projectFileId', opts.projectFileId);

  if (opts.capture) {
    form.append('cx', String(opts.capture.cx));
    form.append('cy', String(opts.capture.cy));
    form.append('screenshotHtml', opts.capture.html);
    form.append('isCompressed', opts.capture.isCompressed ? '1' : '0');
    form.append('screenshotBoundingBox', JSON.stringify(opts.capture.boundingBox));
    form.append('screenshotViewport', JSON.stringify(opts.capture.viewport));
    form.append('screenshotEnvironment', JSON.stringify(opts.capture.screenshotEnvironment));
  } else {
    form.append('cx', String(opts.clickX / window.innerWidth));
    form.append('cy', String(opts.clickY / window.innerHeight));
  }

  if (opts.viewportScreenshot) {
    form.append('screenshot', opts.viewportScreenshot, 'widget-screenshot.png');
  }

  const response = await fetch(url, { method: 'POST', body: form });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(body.message ?? `HTTP ${response.status}`);
  }
}
